import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Plus, Calendar, Pill, FileText, Heart, Activity, Clock,
  User, Phone, Mail, Droplets, AlertTriangle, Edit, Trash2,
  ChevronRight, UserPlus, Stethoscope, Baby, UserCog
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { API } from '../App';
import { MemberDialog, AppointmentDialog, MedicationDialog, RecordDialog } from '../components/family-dashboard/FamilyDialogs';

const FamilyDashboard = ({ user, setUser }) => {
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [memberDashboard, setMemberDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  
  // Form states
  const [memberForm, setMemberForm] = useState({
    name: '', relationship: 'spouse', date_of_birth: '', gender: 'male',
    blood_group: '', phone: '', email: '', allergies: '', chronic_conditions: '',
    current_medications: '', emergency_contact: false, notes: ''
  });
  
  const [appointmentForm, setAppointmentForm] = useState({
    doctor_name: '', specialty: '', hospital_clinic: '', appointment_date: '',
    reason: '', notes: '', reminder_before: 24
  });
  
  const [medicationForm, setMedicationForm] = useState({
    medication_name: '', dosage: '', frequency: 'daily', times: '',
    start_date: '', end_date: '', instructions: '', prescribed_by: ''
  });
  
  const [recordForm, setRecordForm] = useState({
    record_type: 'vitals', title: '', description: '', date: '', data: {}
  });

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberDashboard(selectedMember.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/family/dashboard`);
      setDashboard(response.data);
      setMembers(response.data.members || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load family dashboard');
      setLoading(false);
    }
  };

  const fetchMemberDashboard = async (memberId) => {
    try {
      const response = await axios.get(`${API}/family/dashboard/${memberId}`);
      setMemberDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load member data');
    }
  };

  const handleAddMember = async () => {
    try {
      const data = {
        ...memberForm,
        allergies: memberForm.allergies ? memberForm.allergies.split(',').map(a => a.trim()) : [],
        chronic_conditions: memberForm.chronic_conditions ? memberForm.chronic_conditions.split(',').map(c => c.trim()) : [],
        current_medications: memberForm.current_medications ? memberForm.current_medications.split(',').map(m => m.trim()) : []
      };
      
      if (editingMember) {
        await axios.put(`${API}/family/members/${editingMember.id}`, data);
        toast.success('Family member updated');
      } else {
        await axios.post(`${API}/family/members`, data);
        toast.success('Family member added');
      }
      
      setShowAddMember(false);
      setEditingMember(null);
      resetMemberForm();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving family member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Delete this family member and all their records?')) return;
    
    try {
      await axios.delete(`${API}/family/members/${memberId}`);
      toast.success('Family member deleted');
      if (selectedMember?.id === memberId) setSelectedMember(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Error deleting family member');
    }
  };

  const handleAddAppointment = async () => {
    try {
      const data = {
        ...appointmentForm,
        member_id: selectedMember.id,
        appointment_date: new Date(appointmentForm.appointment_date).toISOString()
      };
      
      await axios.post(`${API}/family/appointments`, data);
      toast.success('Appointment added');
      setShowAddAppointment(false);
      resetAppointmentForm();
      fetchMemberDashboard(selectedMember.id);
      fetchDashboard();
    } catch (error) {
      toast.error('Error adding appointment');
    }
  };

  const handleAddMedication = async () => {
    try {
      const data = {
        ...medicationForm,
        member_id: selectedMember.id,
        times: medicationForm.times.split(',').map(t => t.trim())
      };
      
      await axios.post(`${API}/family/medications`, data);
      toast.success('Medication schedule added');
      setShowAddMedication(false);
      resetMedicationForm();
      fetchMemberDashboard(selectedMember.id);
      fetchDashboard();
    } catch (error) {
      toast.error('Error adding medication');
    }
  };

  const handleAddRecord = async () => {
    try {
      const data = {
        ...recordForm,
        member_id: selectedMember.id
      };
      
      await axios.post(`${API}/family/records`, data);
      toast.success('Health record added');
      setShowAddRecord(false);
      resetRecordForm();
      fetchMemberDashboard(selectedMember.id);
    } catch (error) {
      toast.error('Error adding record');
    }
  };

  const resetMemberForm = () => {
    setMemberForm({
      name: '', relationship: 'spouse', date_of_birth: '', gender: 'male',
      blood_group: '', phone: '', email: '', allergies: '', chronic_conditions: '',
      current_medications: '', emergency_contact: false, notes: ''
    });
  };

  const resetAppointmentForm = () => {
    setAppointmentForm({
      doctor_name: '', specialty: '', hospital_clinic: '', appointment_date: '',
      reason: '', notes: '', reminder_before: 24
    });
  };

  const resetMedicationForm = () => {
    setMedicationForm({
      medication_name: '', dosage: '', frequency: 'daily', times: '',
      start_date: '', end_date: '', instructions: '', prescribed_by: ''
    });
  };

  const resetRecordForm = () => {
    setRecordForm({ record_type: 'vitals', title: '', description: '', date: '', data: {} });
  };

  const getRelationshipIcon = (relationship) => {
    switch (relationship) {
      case 'child': return <Baby className="w-5 h-5" />;
      case 'spouse': return <Heart className="w-5 h-5" />;
      case 'parent': case 'grandparent': return <UserCog className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const getRelationshipColor = (relationship) => {
    switch (relationship) {
      case 'child': return 'from-pink-500 to-rose-500';
      case 'spouse': return 'from-red-500 to-pink-500';
      case 'parent': return 'from-blue-500 to-indigo-500';
      case 'grandparent': return 'from-purple-500 to-violet-500';
      case 'self': return 'from-teal-500 to-cyan-500';
      default: return 'from-gray-500 to-slate-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Navigation user={user} setUser={setUser} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              Family Health Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage health records for your entire family</p>
          </div>
          
          <Button
            onClick={() => { setEditingMember(null); resetMemberForm(); setShowAddMember(true); }}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Add Family Member
          </Button>
        </div>

        {members.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Family Members Added</h3>
              <p className="text-gray-500 mb-6">Add your family members to start tracking their health</p>
              <Button onClick={() => setShowAddMember(true)} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="w-4 h-4 mr-2" /> Add Your First Family Member
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Family Members Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Family Members ({members.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Overview Button */}
                  <div
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      !selectedMember ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                    onClick={() => setSelectedMember(null)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Family Overview</p>
                        <p className="text-xs text-gray-500">View all at once</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Member List */}
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedMember?.id === member.id
                          ? 'bg-blue-50 border-2 border-blue-200'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      onClick={() => setSelectedMember(member)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getRelationshipColor(member.relationship)} flex items-center justify-center text-white`}>
                            {getRelationshipIcon(member.relationship)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{member.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{member.relationship} • {member.age_display}</p>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 ${selectedMember?.id === member.id ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {!selectedMember ? (
                /* Family Overview */
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-blue-50 border-blue-100">
                      <CardContent className="p-4 text-center">
                        <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{dashboard?.summary?.total_members || 0}</p>
                        <p className="text-xs text-blue-600">Family Members</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-100">
                      <CardContent className="p-4 text-center">
                        <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-700">{dashboard?.summary?.upcoming_appointments_count || 0}</p>
                        <p className="text-xs text-green-600">Appointments</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-100">
                      <CardContent className="p-4 text-center">
                        <Pill className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-purple-700">{dashboard?.summary?.active_medications_count || 0}</p>
                        <p className="text-xs text-purple-600">Active Medications</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50 border-orange-100">
                      <CardContent className="p-4 text-center">
                        <FileText className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-orange-700">{dashboard?.summary?.recent_records_count || 0}</p>
                        <p className="text-xs text-orange-600">Recent Records</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Upcoming Appointments & Medications */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-green-600" />
                          Upcoming Appointments
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(dashboard?.upcoming_appointments || []).length === 0 ? (
                          <p className="text-gray-500 text-sm">No upcoming appointments</p>
                        ) : (
                          <div className="space-y-3">
                            {dashboard.upcoming_appointments.map((apt, idx) => (
                              <div key={`item-${idx}`} className="p-3 bg-green-50 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-gray-800">{apt.member_name}</p>
                                    <p className="text-sm text-gray-600">{apt.doctor_name} • {apt.specialty}</p>
                                  </div>
                                  <Badge variant="outline" className="bg-green-100 text-green-700">
                                    {new Date(apt.appointment_date).toLocaleDateString()}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Pill className="w-5 h-5 text-purple-600" />
                          Today's Medications
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(dashboard?.today_medications || []).length === 0 ? (
                          <p className="text-gray-500 text-sm">No medications scheduled</p>
                        ) : (
                          <div className="space-y-3">
                            {dashboard.today_medications.slice(0, 5).map((med, idx) => (
                              <div key={`item-${idx}`} className="p-3 bg-purple-50 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-gray-800">{med.member_name}</p>
                                    <p className="text-sm text-gray-600">{med.medication_name} - {med.dosage}</p>
                                  </div>
                                  <div className="text-right">
                                    {med.times.map((t, i) => (
                                      <Badge key={`item-${i}`} variant="outline" className="ml-1 bg-purple-100 text-purple-700">{t}</Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Family Members Grid */}
                  <Card>
                    <CardHeader>
                      <CardTitle>All Family Members</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className="p-4 border rounded-xl hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => setSelectedMember(member)}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getRelationshipColor(member.relationship)} flex items-center justify-center text-white`}>
                                {getRelationshipIcon(member.relationship)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800">{member.name}</p>
                                <p className="text-sm text-gray-500 capitalize">{member.relationship}</p>
                              </div>
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                              <p>🎂 {member.age_display} old</p>
                              {member.blood_group && <p>🩸 Blood: {member.blood_group}</p>}
                              {member.chronic_conditions?.length > 0 && (
                                <p>⚠️ {member.chronic_conditions.length} condition(s)</p>
                              )}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <Button size="sm" variant="outline" onClick={(e) => {
                                e.stopPropagation();
                                setEditingMember(member);
                                setMemberForm({
                                  ...member,
                                  allergies: member.allergies?.join(', ') || '',
                                  chronic_conditions: member.chronic_conditions?.join(', ') || '',
                                  current_medications: member.current_medications?.join(', ') || ''
                                });
                                setShowAddMember(true);
                              }}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMember(member.id);
                              }}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                /* Individual Member View */
                <>
                  {/* Member Header */}
                  <Card className={`bg-gradient-to-r ${getRelationshipColor(selectedMember.relationship)} text-white`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                            {getRelationshipIcon(selectedMember.relationship)}
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">{selectedMember.name}</h2>
                            <p className="text-white/80 capitalize">{selectedMember.relationship} • {selectedMember.age_display} • {selectedMember.gender}</p>
                            {selectedMember.blood_group && <p className="text-white/70 text-sm">Blood Group: {selectedMember.blood_group}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => {
                            setEditingMember(selectedMember);
                            setMemberForm({
                              ...selectedMember,
                              allergies: selectedMember.allergies?.join(', ') || '',
                              chronic_conditions: selectedMember.chronic_conditions?.join(', ') || '',
                              current_medications: selectedMember.current_medications?.join(', ') || ''
                            });
                            setShowAddMember(true);
                          }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Quick Info */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {selectedMember.allergies?.length > 0 && (
                          <Badge className="bg-white/20 text-white">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Allergies: {selectedMember.allergies.join(', ')}
                          </Badge>
                        )}
                        {selectedMember.chronic_conditions?.length > 0 && (
                          <Badge className="bg-white/20 text-white">
                            <Heart className="w-3 h-3 mr-1" />
                            {selectedMember.chronic_conditions.join(', ')}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-4 gap-2">
                      <TabsTrigger value="overview" className="gap-2"><Activity className="w-4 h-4" /> Overview</TabsTrigger>
                      <TabsTrigger value="appointments" className="gap-2"><Calendar className="w-4 h-4" /> Appointments</TabsTrigger>
                      <TabsTrigger value="medications" className="gap-2"><Pill className="w-4 h-4" /> Medications</TabsTrigger>
                      <TabsTrigger value="records" className="gap-2"><FileText className="w-4 h-4" /> Records</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4">
                      <div className="grid md:grid-cols-3 gap-4">
                        <Card className="bg-green-50">
                          <CardContent className="p-4 text-center">
                            <Calendar className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <p className="text-xl font-bold text-green-700">{memberDashboard?.stats?.upcoming_appointments || 0}</p>
                            <p className="text-xs text-green-600">Upcoming Appointments</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-purple-50">
                          <CardContent className="p-4 text-center">
                            <Pill className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                            <p className="text-xl font-bold text-purple-700">{memberDashboard?.stats?.active_medications || 0}</p>
                            <p className="text-xs text-purple-600">Active Medications</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-50">
                          <CardContent className="p-4 text-center">
                            <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="text-xl font-bold text-blue-700">{memberDashboard?.stats?.total_records || 0}</p>
                            <p className="text-xs text-blue-600">Health Records</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Recent Activity */}
                      <Card className="mt-4">
                        <CardHeader>
                          <CardTitle className="text-lg">Recent Activity</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {(memberDashboard?.recent_records || []).length === 0 ? (
                            <p className="text-gray-500">No recent activity</p>
                          ) : (
                            <div className="space-y-3">
                              {memberDashboard.recent_records.slice(0, 5).map((record, idx) => (
                                <div key={`item-${idx}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800">{record.title}</p>
                                    <p className="text-sm text-gray-500">{record.record_type} • {new Date(record.date).toLocaleDateString()}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="appointments" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Appointments</CardTitle>
                          <Button size="sm" onClick={() => setShowAddAppointment(true)} className="bg-green-600 hover:bg-green-700">
                            <Plus className="w-4 h-4 mr-1" /> Add Appointment
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {(memberDashboard?.appointments || []).length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No appointments scheduled</p>
                          ) : (
                            <div className="space-y-3">
                              {memberDashboard.appointments.map((apt, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-semibold text-gray-800">{apt.doctor_name}</p>
                                      <p className="text-sm text-gray-600">{apt.specialty} • {apt.hospital_clinic}</p>
                                      <p className="text-sm text-gray-500 mt-1">{apt.reason}</p>
                                    </div>
                                    <div className="text-right">
                                      <Badge className={apt.status === 'upcoming' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                        {apt.status}
                                      </Badge>
                                      <p className="text-sm text-gray-600 mt-1">{new Date(apt.appointment_date).toLocaleString()}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="medications" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Medications</CardTitle>
                          <Button size="sm" onClick={() => setShowAddMedication(true)} className="bg-purple-600 hover:bg-purple-700">
                            <Plus className="w-4 h-4 mr-1" /> Add Medication
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {(memberDashboard?.medications || []).length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No medications scheduled</p>
                          ) : (
                            <div className="space-y-3">
                              {memberDashboard.medications.map((med, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-semibold text-gray-800">{med.medication_name}</p>
                                      <p className="text-sm text-gray-600">{med.dosage} • {med.frequency}</p>
                                      {med.instructions && <p className="text-sm text-gray-500 mt-1">{med.instructions}</p>}
                                    </div>
                                    <div className="text-right">
                                      <Badge className={med.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                        {med.is_active ? 'Active' : 'Inactive'}
                                      </Badge>
                                      <div className="flex gap-1 mt-1">
                                        {med.times.map((t, i) => (
                                          <Badge key={`item-${i}`} variant="outline">{t}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="records" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Health Records</CardTitle>
                          <Button size="sm" onClick={() => setShowAddRecord(true)} className="bg-blue-600 hover:bg-blue-700">
                            <Plus className="w-4 h-4 mr-1" /> Add Record
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {(memberDashboard?.recent_records || []).length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No health records</p>
                          ) : (
                            <div className="space-y-3">
                              {memberDashboard.recent_records.map((record, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-semibold text-gray-800">{record.title}</p>
                                      <p className="text-sm text-gray-600">{record.description}</p>
                                    </div>
                                    <div className="text-right">
                                      <Badge variant="outline">{record.record_type}</Badge>
                                      <p className="text-sm text-gray-500 mt-1">{new Date(record.date).toLocaleDateString()}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}

        {/* Dialogs */}
        <MemberDialog open={showAddMember} onOpenChange={setShowAddMember} editingMember={editingMember} memberForm={memberForm} setMemberForm={setMemberForm} onSubmit={handleAddMember} />
        <AppointmentDialog open={showAddAppointment} onOpenChange={setShowAddAppointment} selectedMember={selectedMember} appointmentForm={appointmentForm} setAppointmentForm={setAppointmentForm} onSubmit={handleAddAppointment} />
        <MedicationDialog open={showAddMedication} onOpenChange={setShowAddMedication} selectedMember={selectedMember} medicationForm={medicationForm} setMedicationForm={setMedicationForm} onSubmit={handleAddMedication} />
        <RecordDialog open={showAddRecord} onOpenChange={setShowAddRecord} selectedMember={selectedMember} recordForm={recordForm} setRecordForm={setRecordForm} onSubmit={handleAddRecord} />
      </main>
    </div>
  );
};

export default FamilyDashboard;
